import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hiddenWorksApi, type Site } from '@/api/endpoints';
import {
  Alert,
  Button,
  Card,
  Field,
  FormActions,
  Input,
  Select,
} from '@/components/ui';
import { useFormDraft } from '@/features/drafts';
import { enqueue } from '@/features/upload-queue';
import { ARCHIVE_PRESET, CameraError, capturePhoto, getCurrentFix, LocationError } from '@/native';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/** 常见隐蔽工程类别。工人也可以自己输，列表会累积出各工地的实际叫法。 */
const DEFAULT_CATEGORIES = ['防水', '钢筋', '管线', '基础', '预埋件', '保温'];

/** 被系统打断后要回到的地址（CR-037）。`compose=1` 让表单自己重新展开（CR-042） */
export const HIDDEN_COMPOSE_ROUTE = '/m/hidden?compose=1';

/**
 * 隐蔽工程拍照（CR-042 从隐蔽工程页里抽出来，只在 App 样式里渲染）。
 *
 * 这是需求第 4 条：隐蔽工程必须拍照存档，**随时可拍，不依赖任务**。
 * 照片是工程责任的证据，所以强制现场拍摄（禁相册、禁编辑，约束 5）。
 * 记到的永远是当前工地，表单里没有工地选项（CR-042）。
 */
export function HiddenWorkForm({
  site,
  userId,
  onDone,
  onCancel,
}: {
  site: Site;
  userId: number;
  onDone: () => void;
  /** 表单最下面的「取消」（按钮位置规矩：取消在表单里，不是页头按钮变出来的） */
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();

  /**
   * 表单落盘（CR-037）。
   *
   * 这一页是三张表里最疼的：隐蔽工程**必须先拍照**，而拍照正是安卓回收
   * 这个 Activity 的时刻 —— 工人填完类别和部位、按下拍照，回来一看全空了。
   */
  const draft = useFormDraft(
    'hidden-work',
    userId,
    { category: '', customCategory: '', locationDesc: '', note: '' },
    HIDDEN_COMPOSE_ROUTE,
  );
  const { category, customCategory, locationDesc, note } = draft.fields;
  const shots = draft.shots;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'camera' | 'submit' | null>(null);

  const knownCategories = useQuery({
    queryKey: ['hidden-categories', site.id],
    queryFn: () => hiddenWorksApi.categories(site.id),
  });

  const categories = [...new Set([...DEFAULT_CATEGORIES, ...(knownCategories.data ?? [])])];
  const effectiveCategory = category === '__custom__' ? customCategory.trim() : category;

  async function takePhoto() {
    setError(null);
    setBusy('camera');
    try {
      // 存档规格：长边 4096、质量 95。隐蔽工程照片要看得清钢筋间距、管线走向
      const photo = await capturePhoto(ARCHIVE_PRESET, draft.captureSession('archive'));
      await draft.addShot({
        blob: photo.blob,
        mimeType: photo.mimeType,
        capturedAt: photo.capturedAt,
        isVideo: false,
      });
    } catch (err) {
      if (err instanceof CameraError && err.kind === 'CANCELLED') {
        // 用户自己取消的，不当错误
      } else {
        setError(t('hidden.cameraFailed'));
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (shots.length === 0) {
      setError(t('errors.PHOTO_REQUIRED'));
      return;
    }
    if (!effectiveCategory) {
      setError(t('hidden.categoryRequired'));
      return;
    }
    if (!locationDesc.trim()) {
      setError(t('hidden.locationRequired'));
      return;
    }

    setBusy('submit');
    try {
      // 提交前重新取一次定位：拍照可能是几分钟前的事，
      // 用当时的旧坐标给这条记录背书没有意义
      const fix = await getCurrentFix();

      // 拍照时刻取最后一张，与定位时刻一起提交。
      // 两者差得太远时服务端会拒（阈值定在服务端），
      // 这里不自行拦截 —— 判定统一由服务端做，前端只负责如实上报。
      const latestShot = shots[shots.length - 1]!;

      await enqueue({
        kind: 'HIDDEN_WORK',
        userId,
        siteId: site.id,
        payload: {
          category: effectiveCategory,
          locationDesc: locationDesc.trim(),
          note: note.trim() || undefined,
          capturedAt: latestShot.capturedAt.toISOString(),
          gpsAt: fix.at.toISOString(),
          lat: fix.lat,
          lng: fix.lng,
          gpsAccuracyM: fix.accuracyM ?? undefined,
        },
        files: shots.map((shot) => ({ blob: shot.blob, mimeType: shot.mimeType })),
      });

      // 进了队列就不再是草稿了。**留在这一页**（改之前是跳回首页）——
      // 跳走等于把刚提交的那条藏起来，正是客户说的「列表是空的」；
      // 收起表单之后「我的」列表里立刻多出一条标着「上传中」的（CR-037）
      await draft.clear();
      onDone();
    } catch (err) {
      setError(
        err instanceof LocationError
          ? err.kind === 'DENIED'
            ? t('errors.PUNCH_LOCATION_REQUIRED')
            : t('punch.locateFailed')
          : toMessage(err),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mb-5 p-5">
      <p className="mb-4 text-body leading-relaxed text-ink-3">{t('hidden.subtitle')}</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert>{error}</Alert>}

        {draft.restored && (
          <p className="flex items-center justify-between gap-3 rounded-md bg-surface-2 px-3 py-2 text-caption text-ink-2">
            <span>{t('drafts.restored')}</span>
            <button type="button" onClick={() => void draft.clear()} className="shrink-0 underline">
              {t('drafts.discard')}
            </button>
          </p>
        )}

        <Field label={t('hidden.category')} htmlFor="hw-category" required>
          <Select
            id="hw-category"
            value={category}
            onChange={(e) => draft.setField('category', e.target.value)}
          >
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value="__custom__">{t('hidden.customCategory')}</option>
          </Select>
        </Field>

        {category === '__custom__' && (
          <Field label={t('hidden.customCategory')} htmlFor="hw-custom" required>
            <Input
              id="hw-custom"
              value={customCategory}
              onChange={(e) => draft.setField('customCategory', e.target.value)}
              maxLength={64}
            />
          </Field>
        )}

        <Field
          label={t('hidden.location')}
          htmlFor="hw-location"
          hint={t('hidden.locationHint')}
          required
        >
          <Input
            id="hw-location"
            value={locationDesc}
            onChange={(e) => draft.setField('locationDesc', e.target.value)}
            placeholder={t('hidden.locationPlaceholder')}
            maxLength={200}
          />
        </Field>

        <Field label={t('hidden.note')} htmlFor="hw-note">
          <Input
            id="hw-note"
            value={note}
            onChange={(e) => draft.setField('note', e.target.value)}
            maxLength={1000}
          />
        </Field>

        {/* ---------- 照片 ---------- */}
        <div>
          <p className="mb-2 text-body font-medium text-ink-2">
            {t('hidden.photos')}
            <span className="ml-1 text-danger">*</span>
            {shots.length > 0 && (
              <span className="ml-2 font-normal text-ink-3">
                {t('hidden.photoCount', { count: shots.length })}
              </span>
            )}
          </p>

          {shots.length > 0 && (
            <ul className="mb-3 grid grid-cols-3 gap-2">
              {shots.map((shot) => (
                <li key={shot.id} className="relative">
                  <img
                    src={shot.url}
                    alt=""
                    className="aspect-square w-full rounded-md border border-rule object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => void draft.removeShot(shot.id)}
                    aria-label={t('app.delete')}
                    className="absolute right-1 top-1 rounded-md bg-black/60 px-1.5 text-caption leading-6 text-white"
                  >
                    {t('app.delete')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Button
            type="button"
            variant="secondary"
            block
            onClick={() => void takePhoto()}
            loading={busy === 'camera'}
          >
            {shots.length === 0 ? t('hidden.takePhoto') : t('hidden.takeAnother')}
          </Button>
          <p className="mt-2 text-caption leading-relaxed text-ink-3">{t('hidden.cameraOnlyHint')}</p>
        </div>

        <FormActions
          className="mt-2"
          submitLabel={t('hidden.submit')}
          cancelLabel={t('app.cancel')}
          onCancel={onCancel}
          loading={busy === 'submit'}
        />
      </form>
    </Card>
  );
}
